/**
 * Kickard PHASE 2.1 — Online lobby (simplified one-tap flow).
 *
 *   • CREATE ROOM      → creates the room immediately and enters the match.
 *   • JOIN ROOM        → reveals a 6-char code input + JOIN button.
 *
 *   Network errors are surfaced with a useful message + console diagnostics.
 */
import React, { useState, useEffect } from 'react';
import { saveOnlineSession } from '@/net/onlineSession';
import { ensureGuest, loadGuest } from '@/lib/guestIdentity';
import GuestNameModal from '@/components/GuestNameModal';
import MatchmakingScreen from '@/screens/MatchmakingScreen';
import { track, EVT } from '@/lib/telemetry';
import { setScreen } from '@/lib/sessionTracking';
import { useNavigate } from 'react-router-dom';
import { PremiumBackButton, PremiumHomeButton } from '@/components/PremiumNav';
import { getRankedAvailability } from '@/api/rankedClient';
import { useOnlineActions } from '@/hooks/useOnlineActions';

export default function OnlineLobbyScreen({ onEnterMatch, onBack, onPlayBot }) {
  // Persistent guest identity (Phase 3.1). The lobby no longer asks
  // the user to retype their name — it is set at first-launch and
  // can be edited via the Edit chip below.
  const initialGuest = ensureGuest();
  const [guest, setGuest] = useState(initialGuest);
  const [editingName, setEditingName] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [searching, setSearching] = useState(false);
  // PHASE 4.0.4 — Ranked entry availability (feature-flagged).
  const [rankedAvail, setRankedAvail] = useState(null);
  const navigate = useNavigate();

  // PASS 1C — Room actions now live in a shared hook so PlayHubScreen
  // and this legacy lobby call the SAME proven /rooms/create + /join
  // logic. Behavior, telemetry and session persistence are unchanged.
  const actions = useOnlineActions();
  const busy = actions.busy;
  const error = actions.error;

  const displayName = (guest && guest.displayName) || 'Player';

  // Phase 3.9A — funnel: lobby opened.
  useEffect(() => {
    setScreen('online_lobby');
    track(EVT.ONLINE_LOBBY_OPENED);
  }, []);

  // PHASE 4.0.4 — check Ranked availability once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const a = await getRankedAvailability();
      if (!cancelled) setRankedAvail(a);
    })();
    return () => { cancelled = true; };
  }, []);

  function handleMatched(session) {
    // session: { roomCode, playerId, token, name, opponentName }
    saveOnlineSession({
      roomCode: session.roomCode,
      playerId: session.playerId,
      token: session.token,
      name: session.name,
      opponentName: session.opponentName,
    });
    setSearching(false);
    onEnterMatch({
      roomCode: session.roomCode,
      playerId: session.playerId,
      token: session.token,
      name: session.name,
      opponentName: session.opponentName,
    });
  }

  async function createRoom() {
    const session = await actions.createRoom();
    if (session) onEnterMatch(session);
  }

  async function joinRoom() {
    const session = await actions.joinRoom(joinCode);
    if (session) onEnterMatch(session);
  }

  return (
    <>
      {searching && (
        <MatchmakingScreen
          guest={guest}
          onMatched={handleMatched}
          onCancel={() => setSearching(false)}
          onPlayBot={onPlayBot}
        />
      )}
      {!searching && (
    <div data-testid="online-lobby" className="kickard-frame stadium-bg flex flex-col items-center justify-center gap-8 pb-8 px-6 relative overflow-hidden k-safe-top">
      <div className="pointer-events-none absolute inset-0" aria-hidden style={{
        background: 'radial-gradient(ellipse at 50% 16%, hsl(46 95% 60% / 0.14) 0%, transparent 60%)',
      }} />
      <div className="kpn-row kpn-row--lobby z-10">
        <PremiumBackButton onClick={onBack} testid="lobby-back-btn" />
        <div className="kpn-center flex flex-col items-center">
          <span className="font-heading text-3xl text-[hsl(var(--k-gold-bright))]" style={{ letterSpacing: '0.22em' }}>ONLINE</span>
          <span className="font-body text-[10px] text-white/45 uppercase tracking-[0.34em] mt-0.5">Quick or friend match</span>
        </div>
        <span className="kpn-end"><PremiumHomeButton testid="lobby-home-btn" /></span>
      </div>

      {/* One-line broadcast hint — no clutter */}
      <div className="flex items-center gap-3 relative z-10">
        <span className="block w-8 h-px bg-[hsl(var(--sb-rule)/0.5)]" />
        <span className="font-body text-[10px] text-white/55 uppercase tracking-[0.3em] text-center">Match instantly or play a friend</span>
        <span className="block w-8 h-px bg-[hsl(var(--sb-rule)/0.5)]" />
      </div>

      <div className="w-full max-w-sm flex flex-col gap-3 relative z-10">
        {/* Playing-as chip — replaces the previous name input */}
        <div
          data-testid="lobby-playing-as"
          className="w-full flex items-center justify-between px-4 py-3 rounded bg-white/5 border border-white/10"
        >
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-mono text-[9px] text-white/45 tracking-[0.28em] uppercase">
              Playing as
            </span>
            <span
              data-testid="lobby-playing-as-name"
              className="font-heading text-lg text-white tracking-[0.06em] truncate"
            >
              {displayName}
            </span>
          </div>
          <button
            type="button"
            data-testid="lobby-edit-name-btn"
            onClick={() => setEditingName(true)}
            className="back-chip"
            aria-label="Edit name"
          >
            <span>EDIT</span>
          </button>
        </div>

        {/* QUICK MATCH section — primary path */}
        {!showJoin && (
          <div className="flex flex-col gap-1">
            <button
              data-testid="quick-match-btn"
              className="btn-primary"
              onClick={() => { actions.clearError(); setSearching(true); }}
              disabled={busy}
            >
              QUICK MATCH
            </button>
            <div className="font-mono text-[10px] text-white/45 tracking-[0.24em] uppercase text-center mt-1">
              Find an opponent automatically
            </div>
          </div>
        )}

        {/* PHASE 4.0.4 — RANKED section. Rendered unless the backend
            has AUTHORITATIVELY reported Ranked closed. A transient
            fetch failure (!ok) or still-loading state must NOT hide
            the Ranked entry — this matches Home / Play Hub semantics.
            PHASE 5.0B Astrix fix. */}
        {!showJoin && !(rankedAvail?.ok === true
                        && (!rankedAvail.enabled || !rankedAvail.activeSeasonExists)) && (
          <div className="flex flex-col gap-1" data-testid="ranked-entry-card">
            <button
              data-testid="ranked-enter-btn"
              className="btn-secondary"
              onClick={() => navigate('/ranked')}
              disabled={busy}
            >
              RANKED PRESEASON
            </button>
            <div className="font-mono text-[10px] text-amber-300/70 tracking-[0.24em] uppercase text-center mt-1">
              Competitive 1v1 · ratings may reset before Season 1
            </div>
          </div>
        )}

        {/* Section divider */}
        {!showJoin && (
          <div className="flex items-center gap-3 my-1">
            <span className="block flex-1 h-px bg-white/8" />
            <span className="font-mono text-[9px] text-white/35 tracking-[0.32em] uppercase">
              or friend match
            </span>
            <span className="block flex-1 h-px bg-white/8" />
          </div>
        )}

        {!showJoin && (
          <>
            <button
              data-testid="lobby-create-btn"
              className="btn-secondary"
              onClick={createRoom}
              disabled={busy}
            >
              {busy ? 'CREATING…' : 'CREATE ROOM'}
            </button>
            <button
              data-testid="lobby-join-btn"
              className="btn-secondary"
              onClick={() => { setShowJoin(true); actions.clearError(); }}
              disabled={busy}
            >
              JOIN ROOM
            </button>
          </>
        )}

        {showJoin && (
          <>
            <input
              data-testid="lobby-code-input"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, '').slice(0, 6))}
              placeholder="ROOM CODE"
              maxLength={6}
              className="w-full px-4 py-3 rounded bg-white/5 border border-white/10 text-white text-center font-mono text-2xl tracking-[0.35em] outline-none focus:border-[hsl(var(--k-gold))]"
              disabled={busy}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              data-testid="lobby-confirm-join-btn"
              className="btn-primary"
              onClick={joinRoom}
              disabled={busy || joinCode.trim().length !== 6}
            >
              {busy ? 'JOINING…' : 'JOIN ROOM'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => { setShowJoin(false); setJoinCode(''); actions.clearError(); }}
              disabled={busy}
            >
              ← BACK
            </button>
          </>
        )}

        {error && (
          <div data-testid="lobby-error" className="text-red-300 text-center text-sm mt-2 px-2">
            {error}
          </div>
        )}
      </div>

      {editingName && (
        <GuestNameModal
          mode="edit"
          initial={displayName}
          onSaved={(g) => {
            setGuest(g || loadGuest());
            setEditingName(false);
          }}
          onCancel={() => setEditingName(false)}
        />
      )}
    </div>
      )}
    </>
  );
}
